/*
 * Copyright The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRouteRefParams } from '@backstage/core-plugin-api';
import { useLocation } from 'react-router-dom';
import {
  Page,
  Header,
  Content,
  ErrorBoundary,
} from '@backstage/core-components';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { themeId } from '../consts';
import { packageInstallRouteRef } from '../routes';
import { ReactQueryProvider } from '../components/ReactQueryProvider';
import { usePackage } from '../hooks/usePackage';
import { ExtensionsPackageEditContentLoader } from '../components/ExtensionsPackageEditContent';

const PackageEditHeader = () => {
  const params = useRouteRefParams(packageInstallRouteRef);
  const location = useLocation();

  const pkg = usePackage(params.namespace, params.name);

  const displayName = pkg.data?.metadata?.title ?? params.name;
  const title =
    location?.state?.viewOnly || !pkg.data?.spec?.dynamicArtifact
      ? displayName
      : `Edit ${displayName}`;
  const baseLink = '/extensions/installed-packages';
  const preserved = new URLSearchParams(location.search);
  const packageLink = preserved.size ? `${baseLink}?${preserved}` : baseLink;

  const theme = useTheme();
  const headerBorderBottomColor =
    theme.palette.mode === 'dark' ? '#A3A3A3' : '#C7C7C7';

  return (
    // TODO: add header border color and Breadcrumbs styles in theme plugin
    <Box
      sx={{
        display: 'contents',
        width: '100%',
        '& > header': {
          borderBottom: `1px solid ${headerBorderBottomColor}`,
        },
        '& > header div[class*="BreadcrumbsCurrentPage-root"] > p': {
          fontStyle: 'italic',
        },
      }}
    >
      <Header title={title} type="Packages" typeLink={packageLink} />
    </Box>
  );
};

export const ExtensionsPackageInstallPage = () => {
  return (
    <ReactQueryProvider>
      <Page themeId={themeId}>
        <PackageEditHeader />
        <Content>
          <ErrorBoundary>
            {/* Force remount on navigation within same route to reseed editor */}
            <ExtensionsPackageEditContentLoader />
          </ErrorBoundary>
        </Content>
      </Page>
    </ReactQueryProvider>
  );
};
